import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  FileText,
  Package,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/App";

export default function ToolkitDetail() {
  const { slug } = useParams();
  const [toolkit, setToolkit] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const toolkitRes = await api.get(`/toolkits/${slug}`);
        setToolkit(toolkitRes.data);

        // Fetch related protocols
        if (toolkitRes.data.protocol_ids?.length > 0) {
          const protocolsRes = await api.get("/protocols");
          const relatedProtocols = protocolsRes.data.filter(
            (p) => toolkitRes.data.protocol_ids.includes(p.id)
          );
          setProtocols(relatedProtocols);
        }

        // Fetch related assets
        if (toolkitRes.data.asset_ids?.length > 0) {
          const assetsRes = await api.get("/assets");
          const relatedAssets = assetsRes.data.filter(
            (a) => toolkitRes.data.asset_ids.includes(a.id)
          );
          setAssets(relatedAssets);
        }
      } catch (error) {
        // Error fetching toolkit
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const trackDownload = async (resourceId, resourceType, title) => {
    try {
      await api.post("/analytics/event", {
        event_type: `${resourceType}_download`,
        resource_id: resourceId,
        resource_type: resourceType,
        metadata: { toolkit_id: toolkit?.id },
      });
      toast.success(`Downloaded: ${title}`);
    } catch (error) {
      // Error tracking download - silent fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!toolkit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Toolkit Not Found</h1>
          <Link to="/toolkits">
            <Button>Back to Toolkits</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="toolkit-detail-page">
      {/* Header */}
      <section 
        className="py-16 text-white"
        style={{ backgroundColor: toolkit.color || "#10B981" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/toolkits" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6"
            data-testid="back-to-toolkits"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Toolkits
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-6"
          >
            <div className="w-16 h-16 bg-white/20 rounded-sm flex items-center justify-center flex-shrink-0">
              <Package className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="toolkit-title">
                {toolkit.title}
              </h1>
              <p className="text-lg text-white/90 max-w-2xl">
                {toolkit.summary}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Included Protocols */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  Included Protocols
                </h2>
                {protocols.length > 0 ? (
                  <div className="space-y-4" data-testid="included-protocols">
                    {protocols.map((protocol) => (
                      <Link
                        key={protocol.id}
                        to={`/protocols/${protocol.slug}`}
                        className="block p-4 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300"
                        data-testid={`protocol-link-${protocol.slug}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`badge-urgency-${protocol.urgency} px-2 py-0.5 rounded-full text-xs font-mono uppercase`}>
                                {protocol.urgency}
                              </span>
                            </div>
                            <h3 className="font-bold text-slate-900">
                              {protocol.title}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">
                              {protocol.summary}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-600 bg-slate-50 p-4 rounded-sm">
                    No protocols included in this toolkit yet.
                  </p>
                )}
              </div>

              {/* Downloadable Assets - only show when assets exist */}
              {assets.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Downloadable Assets
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-testid="toolkit-assets">
                  {assets.map((asset) => (
                    <a
                      key={asset.id}
                      href={asset.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackDownload(asset.id, "asset", asset.title)}
                      className="block p-4 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300"
                      data-testid={`asset-download-${asset.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-900">
                            {asset.title}
                          </h3>
                          <p className="text-sm text-slate-500 uppercase">
                            {asset.file_type} • v{asset.version}
                          </p>
                        </div>
                        <Download className="w-5 h-5 text-slate-400" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
